/* oxlint-disable no-nested-ternary */
import { faMinus } from '@fortawesome/pro-light-svg-icons';
import { Action, Card, cn, Icon, Modal } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { useState } from 'react';
import { match } from 'ts-pattern';

/**
 * Shared by the return/warranty and shipping claim pages. The two assessments differ only in
 * their factor enum, which is never rendered — level, sentiment and justification are common.
 */
type RiskAssessment =
  | CrewMerchantUi.ClaimRiskAssessment
  | CrewMerchantUi.ShippingClaimRiskAssessment;

const barStyles =
  'relative flex h-2 w-full overflow-hidden rounded-full bg-neutral-200';

function LoadingBar() {
  return (
    <div className={cn(barStyles)}>
      <div className="absolute inset-0 animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-neutral-200 via-neutral-300 to-neutral-200" />
    </div>
  );
}

function RiskBar({ level }: { level?: 'Low' | 'Medium' | 'High' }) {
  const segmentClasses = {
    Low: 'bg-emerald-600',
    Medium: 'bg-amber-500',
    High: 'bg-red-600',
  } as const;

  const activeIndex = level === 'Low' ? 0 : level === 'Medium' ? 1 : 2;

  if (!level)
    return <p className="text-xs text-neutral-500">Analysis not available</p>;

  return (
    <div className="w-full">
      <div className={cn(barStyles)}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              'flex-1 transition-all duration-300',
              i === activeIndex ? segmentClasses[level] : 'bg-transparent',
            )}
          />
        ))}
      </div>

      <div className="mt-1 flex justify-between text-sm text-neutral-700">
        <span
          className={cn(level === 'Low' && 'font-semibold text-emerald-600')}
        >
          Low
        </span>
        <span
          className={cn(level === 'Medium' && 'font-semibold text-amber-600')}
        >
          Medium
        </span>
        <span className={cn(level === 'High' && 'font-semibold text-red-600')}>
          High
        </span>
      </div>
    </div>
  );
}

function SentimentIcon({
  sentiment,
}: {
  sentiment: RiskAssessment['facts'][number]['sentiment'];
}) {
  return match(sentiment)
    .with('Critical', () => <Icon icon="error" className="text-red-600" />)
    .with('Positive', () => (
      <Icon icon="success" className="text-emerald-600" />
    ))
    .with('Negative', () => <Icon icon="warning" className="text-amber-600" />)
    .with('Neutral', () => <Icon icon={faMinus} className="text-neutral-500" />)
    .exhaustive();
}

function RiskBreakdown({
  riskAssessment,
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
  riskAssessment: RiskAssessment;
}) {
  const critical = riskAssessment.facts.filter(
    ({ sentiment }) => sentiment === 'Critical',
  );
  const neutral = riskAssessment.facts.filter(
    ({ sentiment }) => sentiment === 'Neutral',
  );
  const positive = riskAssessment.facts.filter(
    ({ sentiment }) => sentiment === 'Positive',
  );
  const negative = riskAssessment.facts.filter(
    ({ sentiment }) => sentiment === 'Negative',
  );

  const facts = [...positive, ...critical, ...negative, ...neutral];

  return (
    <Modal show={show} onClose={onClose} title="About this claim" size="lg">
      {facts.map(({ factor, justification, sentiment }) => (
        <div key={factor} className="flex items-center gap-2">
          <SentimentIcon sentiment={sentiment} />
          <p className="text-sm text-neutral-500">{justification}</p>
        </div>
      ))}
      <div>
        <p className="text-xs text-neutral-500">
          Learn more about how Corso identifies and scores risk factors{' '}
          <Action
            variant="link"
            to="https://help.corso.com/platform/claim-risk"
            target="_blank"
            rel="noreferrer"
          >
            here.
          </Action>
        </p>
      </div>
    </Modal>
  );
}

export function RiskAssessmentCard({
  riskAssessment,
  isPending,
}: {
  riskAssessment: RiskAssessment | null | undefined;
  isPending: boolean;
}) {
  const [showRiskBreakdown, setShowRiskBreakdown] = useState(false);

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <Card.Line>
          <Card.Heading>Claim risk</Card.Heading>

          <Action
            disabled={isPending || !riskAssessment}
            icon="search"
            accessibilityLabel="Claim risk"
            variant="ghost"
            onClick={() => setShowRiskBreakdown(true)}
          />
        </Card.Line>

        {isPending ? <LoadingBar /> : <RiskBar level={riskAssessment?.level} />}

        {riskAssessment && (
          <RiskBreakdown
            riskAssessment={riskAssessment}
            show={showRiskBreakdown}
            onClose={() => setShowRiskBreakdown(false)}
          />
        )}
      </div>
    </Card>
  );
}
